import { useEffect, useMemo } from 'react';
import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';
import useColumnsLeadsList from '../tableColumns/useColumnsLeadsList';
import useLeadsStore from '../../../store/LeadsStore';
import { ReactSelectOption } from '../../../types';
import { useAuth } from '../../../hooks/useAuth';
import { LeadsModalModeEnum } from '../../../api/models/Enums';
import _ from 'lodash';
import { LeadSearchParams, RoleEnum } from '../../../api';
import useModalStore from '../../../store/ModalStore';
import useTableStore from 'src/store/TableStore';
import LeadsActions from './LeadsActions';
import LeadsTable from './LeadsTable';
import { useLeadDetailHref } from '../hooks/useLeadDetailHref';

interface LeadsListProps {
	data: any[];
	isVisible?: boolean;
	isLoading: boolean;
	totalCount?: number;
	error?: any;
	loadFn: (filter: Partial<LeadSearchParams>) => void;
	filter?: Partial<LeadSearchParams>;
	onResetFilter?: () => void;
	activeFiltersCount?: number;
}

const LeadsList: React.FC<LeadsListProps> = ({
	data,
	totalCount,
	isLoading,
	isVisible,
	error,
	loadFn,
	filter,
	onResetFilter,
	activeFiltersCount,
}) => {
	const {
		setUserSalesOptions,
		setActiveLead,
		dealers,
		setCurrentUserIsRefDealer,
		currentUserIsRefDealer,
		salesUsers,
		activeLead,
	} = useLeadsStore();
	const { openModal } = useModalStore();
	const { userRole, user, userCanReassignLead, userReferencePartnerList } =
		useAuth();

	const { clearSelectedRows } = useTableStore();
	const leadDetailHref = useLeadDetailHref();
	const rawColumns = useColumnsLeadsList();
	const columns = useMemo(() => rawColumns, [rawColumns]);

	/**
	 * Use Case: gestione riassegnazione lead per utenti SALES
	 *
	 * Descrizione:
	 * Questo useEffect gestisce la logica per consentire a un utente con ruolo "SALES",
	 * che è anche referente abilitato di un Dealer, di visualizzare e riassegnare le lead
	 * ad altri utenti Sales appartenenti allo stesso Dealer.
	 */
	useEffect(() => {
		if (
			![
				RoleEnum.SALES,
				RoleEnum.SALES_DEALER,
				RoleEnum.SALES_INTERNAL,
			].includes(userRole)
		) {
			return;
		}

		const currentUserRefPartner = userReferencePartnerList?.[0];
		if (!currentUserRefPartner) {
			setUserSalesOptions([]);
			setCurrentUserIsRefDealer(false);
			return;
		}

		const currentUserRefPartnerMatches = [
			currentUserRefPartner.toLowerCase(),
			currentUserRefPartner.replace(/ /g, '_').toLowerCase(),
		];

		const salesDealer = dealers.find((dealer) =>
			currentUserRefPartnerMatches.includes(
				dealer.ragione_sociale?.toLowerCase()
			)
		);

		if (!salesDealer) {
			setUserSalesOptions([]);
			setCurrentUserIsRefDealer(false);
			return;
		}

		const dealerReferents = salesDealer.referenti_dealer || [];
		const isCurrentUserRefDealer = dealerReferents.some(
			(username) => user.username?.toLowerCase() === username?.toLowerCase()
		);

		setCurrentUserIsRefDealer(isCurrentUserRefDealer);

		if (!isCurrentUserRefDealer) {
			setUserSalesOptions([]);
			return;
		}

		const eligibleSalesUsers = (salesUsers || []).filter(
			(salesUser) =>
				(salesUser.reference_partner || []).some(
					(ref) => ref?.toLowerCase() === currentUserRefPartner?.toLowerCase()
				) && salesUser.username !== user.username
		);

		const salesOptions: ReactSelectOption[] = eligibleSalesUsers.map(
			(salesUser) => ({
				label: `${salesUser.family_name} ${salesUser.given_name}`,
				value: salesUser.username ?? '',
			})
		);

		setUserSalesOptions(salesOptions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.username, userRole, userReferencePartnerList, dealers, salesUsers]);

	const handleDoubleClick = (row?: any) => {
		if (!row) return;

		if (activeLead?.Id !== row.Id) {
			openModal(LeadsModalModeEnum.DETAILS);
			setActiveLead(row);
		}
	};

	const hiddenColumns = useMemo(() => {
		if (
			![
				RoleEnum.MASTER,
				RoleEnum.BACKOFFICE,
				RoleEnum.SALES,
				RoleEnum.SALES_DEALER,
				RoleEnum.SALES_INTERNAL,
				RoleEnum.AREA_MANAGER,
			].includes(userRole)
		) {
			return ['checkboxes'];
		}

		if (
			[RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(
				userRole
			)
		) {
			return currentUserIsRefDealer ? [] : ['SalesUser', 'checkboxes'];
		}

		if (
			[RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole) &&
			!userCanReassignLead
		) {
			return ['checkboxes'];
		}
	}, [userRole, userCanReassignLead, currentUserIsRefDealer]);

	useEffect(() => {
		return () => {
			clearSelectedRows?.(false);
		};
	}, [clearSelectedRows]);

	if (!isVisible) return null;

	return (
		<>
			<LeadsActions />
			<ErrorBoundary>
				<LeadsTable
					onResetFilter={onResetFilter}
					activeFiltersCount={activeFiltersCount}
					filter={filter}
					dblClickCallback={handleDoubleClick}
					rowHref={(lead) => leadDetailHref(lead.Id)}
					hiddenColumns={hiddenColumns}
					totalCount={totalCount || 0}
					columns={columns}
					loadFn={loadFn}
					error={error}
					data={data}
					loading={isLoading}
				/>
			</ErrorBoundary>
		</>
	);
};

export default LeadsList;
